'use client';

import { useEffect, useRef, useState } from 'react';

import { cn } from '@kit/ui/utils';

import styles from './pacific-supply-map.module.css';

const steps = [
  ['01', 'Factory in China', 'Production tracked on the PO'],
  ['02', 'On the water', 'Freight, duty, and ETA in one place'],
  ['03', 'Your warehouse or FBA', 'Landed cost ready when it arrives'],
] as const;

export function PacificSupplyMap({
  title = 'See Every Shipment Move From China To Your Warehouse',
  destination = 'US warehouse',
  eyebrow = 'From factory to FBA, in one picture',
  headerBrand = 'SUPPLYAUTOMATE',
  headerMark = 'S',
  headerAccent,
  footnote,
  className,
}: {
  title?: string;
  destination?: string;
  eyebrow?: string;
  /** Card header wordmark. Homepage keeps SUPPLYAUTOMATE. */
  headerBrand?: string;
  headerMark?: string;
  /** Icon colour for a partner example. Homepage keeps the stylesheet colour. */
  headerAccent?: string;
  footnote?: string;
  className?: string;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    const card = cardRef.current;

    if (!card) return;

    if (!('IntersectionObserver' in window)) {
      const frame = requestAnimationFrame(() => setHasEntered(true));
      return () => cancelAnimationFrame(frame);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setHasEntered(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(card);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className={cn(
        'bg-section-switch border-border border-y py-12 lg:py-16',
        className,
      )}
      aria-labelledby="pacific-map-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto mb-10 flex max-w-3xl flex-col items-center gap-3.5 text-center">
          <span className="text-muted-foreground text-[11px] font-bold tracking-[0.16em] uppercase">
            {eyebrow}
          </span>
          <h2
            id="pacific-map-heading"
            className="text-3xl font-bold tracking-tight text-balance sm:text-4xl"
          >
            {title}
          </h2>
          <p className="text-muted-foreground max-w-2xl text-lg leading-[1.6]">
            One consolidated view of production, freight, and delivery from
            China to your {destination}.
          </p>
        </div>

        <div
          ref={cardRef}
          className={`${styles.card} ${hasEntered ? styles.active : ''}`}
        >
          <div className={styles.cardHeader}>
            <div className={styles.brand}>
              <span
                className={styles.brandIcon}
                style={headerAccent ? { background: headerAccent } : undefined}
                aria-hidden="true"
              >
                {headerMark}
              </span>
              {headerBrand}
            </div>
            <span className={styles.headerNote}>CHINA TO USA · ONE VIEW</span>
          </div>

          <div className={styles.stage}>
            <svg
              className={styles.worldMap}
              viewBox="0 0 1200 520"
              xmlns="http://www.w3.org/2000/svg"
              role="img"
              aria-label="Pacific-centered world map showing a shipment route from China on the left to the United States on the right"
            >
              <image
                href="/images/pacific-world-map.svg"
                width="1200"
                height="520"
              />
              <path
                className={styles.routeHalo}
                d="M405 180 C525 38 741 43 900 155"
              />
              <path
                className={styles.route}
                d="M405 180 C525 38 741 43 900 155"
              />
              <g className={styles.marker} aria-hidden="true">
                <circle r="14" fill="#6d28d9" fillOpacity=".16" />
                <circle r="6" fill="#6d28d9" stroke="#fff" strokeWidth="2" />
              </g>
              <g className={styles.node}>
                <circle
                  cx="405"
                  cy="180"
                  r="16"
                  fill="#6d28d9"
                  fillOpacity=".12"
                />
                <circle
                  cx="405"
                  cy="180"
                  r="7"
                  fill="#51209b"
                  stroke="#fff"
                  strokeWidth="2"
                />
                <circle
                  cx="900"
                  cy="155"
                  r="17"
                  fill="#6d28d9"
                  fillOpacity=".12"
                />
                <circle
                  cx="900"
                  cy="155"
                  r="8"
                  fill="#51209b"
                  stroke="#fff"
                  strokeWidth="2"
                />
              </g>
              <text className={styles.place} x="352" y="216">
                CHINA
              </text>
              <text className={styles.place} x="922" y="146">
                USA
              </text>
            </svg>

            <ol className={styles.callouts}>
              <li className={`${styles.callout} ${styles.china}`}>
                <small>01 · FACTORY IN CHINA</small>
                <strong>Production tracked on the PO</strong>
              </li>
              <li className={`${styles.callout} ${styles.ocean}`}>
                <small>02 · ON THE WATER</small>
                <strong>Freight, duty, and ETA in one place</strong>
              </li>
              <li className={`${styles.callout} ${styles.usa}`}>
                <small>03 · YOUR WAREHOUSE OR FBA</small>
                <strong>
                  Landed cost ready when it <mark>arrives</mark>
                </strong>
              </li>
            </ol>
          </div>

          <ol className={styles.steps}>
            {steps.map(([number, title, description]) => (
              <li key={number}>
                <span>{number}</span>
                <div>
                  <strong>{title}</strong>
                  <p>{description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {footnote ? (
          <p className="text-muted-foreground mx-auto mt-6 max-w-2xl text-center text-sm leading-6">
            {footnote}
          </p>
        ) : null}
      </div>
    </section>
  );
}
